/**
 * Coordinate conversion utilities for PDF signing.
 *
 * CRITICAL CONCEPT:
 *   PDF coordinate system (used by pdf-lib and the PDF spec):
 *     - Origin (0, 0) is at the BOTTOM-LEFT corner of the page.
 *     - Y axis points UP.
 *     - Units are "points" (1 point = 1/72 inch).
 *
 *   Browser / HTML coordinate system (used by mouse events and canvas):
 *     - Origin (0, 0) is at the TOP-LEFT corner of the element.
 *     - Y axis points DOWN.
 *     - Units are CSS pixels.
 *
 * The pdfjs library gives us a "viewport" for each page at a chosen scale.
 * viewport.width  and viewport.height are the rendered pixel dimensions.
 * viewport.scale is the multiplier applied to PDF points.
 *
 * Conversion rules:
 *   pdfX = viewerX / scale
 *   pdfY = pageHeight - (viewerY / scale)
 *
 *   viewerX = pdfX * scale
 *   viewerY = (pageHeight - pdfY) * scale
 *
 * These helpers are deliberately pure and receive scale + page dimensions
 * explicitly so they work correctly across pages of different sizes and
 * at any zoom level we implement in the future.
 */

import type { SigningField } from '../types/fields';
import { measureTextFieldPdfSize } from './textFieldMeasure';

/**
 * Result of converting a viewer click/position into PDF coordinates.
 */
export interface PdfCoordinates {
  x: number;   // distance from left edge in PDF points
  y: number;   // distance from bottom edge in PDF points
}

/**
 * Convert a position from the viewer overlay (top-left origin, pixels)
 * into PDF coordinate space (bottom-left origin, points).
 *
 * @param viewerX   Pixel X relative to top-left of the rendered page
 * @param viewerY   Pixel Y relative to top-left of the rendered page (0 = top)
 * @param pageHeight Height of the page in PDF points
 * @param scale     The scale at which pdfjs rendered this page (pixels per point)
 */
/**
 * Map between PDF points and the overlay's *displayed* CSS pixel size.
 * When the page is CSS-scaled (e.g. max-width on the viewer), scaleX/scaleY differ
 * from the nominal render scale — using those keeps WYSIWYG with the visible PDF.
 */
export function getDisplayScales(
  displayWidth: number,
  displayHeight: number,
  pageWidth: number,
  pageHeight: number
): { scaleX: number; scaleY: number } {
  if (displayWidth <= 0 || displayHeight <= 0 || pageWidth <= 0 || pageHeight <= 0) {
    return { scaleX: 1, scaleY: 1 };
  }
  return {
    scaleX: displayWidth / pageWidth,
    scaleY: displayHeight / pageHeight,
  };
}

/**
 * Pick displayed CSS pixels for overlay coordinate math.
 * After a zoom change, layoutSize can still hold the prior scale's dimensions
 * (smaller than nominal) for one frame. Prefer nominal until the live measurement
 * catches up, unless the committed layout box confirms genuine CSS shrink.
 */
export function resolveDisplayDimensions(
  nominalWidth: number,
  nominalHeight: number,
  layoutSize: { width: number; height: number } | null,
  committedWidth?: number,
  committedHeight?: number
): { width: number; height: number } {
  if (!layoutSize) {
    return { width: nominalWidth, height: nominalHeight };
  }

  const cachedSmaller =
    layoutSize.width < nominalWidth - 1.5 && layoutSize.height < nominalHeight - 1.5;

  if (!cachedSmaller) {
    return { width: layoutSize.width, height: layoutSize.height };
  }

  const committedW = committedWidth ?? layoutSize.width;
  const committedH = committedHeight ?? layoutSize.height;
  const genuineShrink =
    committedW < nominalWidth - 1.5 || committedH < nominalHeight - 1.5;

  if (genuineShrink) {
    return { width: layoutSize.width, height: layoutSize.height };
  }

  return { width: nominalWidth, height: nominalHeight };
}

/**
 * Pick scales for overlay ↔ PDF conversion.
 * Uses live getBoundingClientRect sizes when the surface is CSS-shrunk (display < nominal).
 * Otherwise uses renderScale so zoom changes never read stale cached display pixels.
 */
export function resolveViewerScales(
  pageWidth: number,
  pageHeight: number,
  renderScale: number,
  liveDisplayWidth: number,
  liveDisplayHeight: number
): { scaleX: number; scaleY: number } {
  const nominalW = pageWidth * renderScale;
  const nominalH = pageHeight * renderScale;
  const cssShrunk =
    liveDisplayWidth > 0 &&
    liveDisplayHeight > 0 &&
    (liveDisplayWidth < nominalW - 1.5 || liveDisplayHeight < nominalH - 1.5);

  if (cssShrunk) {
    return getDisplayScales(liveDisplayWidth, liveDisplayHeight, pageWidth, pageHeight);
  }
  if (renderScale > 0) {
    return { scaleX: renderScale, scaleY: renderScale };
  }
  return { scaleX: 1, scaleY: 1 };
}

export function viewerToPdfCoords(
  viewerX: number,
  viewerY: number,
  pageHeight: number,
  scale: number,
  pageWidth?: number,
  displayWidth?: number,
  displayHeight?: number
): PdfCoordinates {
  const scales =
    displayWidth != null &&
    displayHeight != null &&
    pageWidth != null &&
    pageWidth > 0 &&
    pageHeight > 0
      ? resolveViewerScales(pageWidth, pageHeight, scale, displayWidth, displayHeight)
      : { scaleX: scale, scaleY: scale };

  if (scales.scaleX <= 0 || scales.scaleY <= 0 || pageHeight <= 0) {
    return { x: 0, y: 0 };
  }

  const pdfX = viewerX / scales.scaleX;
  const pdfY = pageHeight - viewerY / scales.scaleY;

  return {
    x: Math.max(0, pdfX),
    y: Math.max(0, pdfY),
  };
}

/**
 * Convert a field stored in PDF coordinates back into viewer pixel coordinates
 * so we can absolutely position a <div> on top of the rendered canvas.
 *
 * @returns Object with top, left, width, height in pixels ready for style.
 */
export function pdfToViewerCoords(
  field: SigningField,
  pageHeight: number,
  scale: number,
  pageWidth?: number,
  displayWidth?: number,
  displayHeight?: number
): { top: number; left: number; width: number; height: number } {
  const scales =
    displayWidth != null &&
    displayHeight != null &&
    pageWidth != null &&
    pageWidth > 0 &&
    pageHeight > 0
      ? resolveViewerScales(pageWidth, pageHeight, scale, displayWidth, displayHeight)
      : { scaleX: scale, scaleY: scale };

  if (scales.scaleX <= 0 || scales.scaleY <= 0 || pageHeight <= 0) {
    return { top: 0, left: 0, width: 0, height: 0 };
  }

  const left = field.x * scales.scaleX;
  const top = (pageHeight - field.y - field.height) * scales.scaleY;

  return {
    top: Math.max(0, top),
    left: Math.max(0, left),
    width: Math.max(10, field.width * scales.scaleX),
    height: Math.max(10, field.height * scales.scaleY),
  };
}

/**
 * Given a desired field size in pixels on screen (e.g. signature box 220×70),
 * convert it to PDF points at the current render scale.
 * This lets us create fields that look the same physical size regardless of
 * how we chose to render the PDF page.
 */
export function pixelsToPdfSize(
  pixelWidth: number,
  pixelHeight: number,
  scale: number
): { width: number; height: number } {
  if (scale <= 0) return { width: 80, height: 28 };
  return {
    width: pixelWidth / scale,
    height: pixelHeight / scale,
  };
}

/**
 * Default sizes (in pixels at 1.5x typical render scale) for each field type.
 * These are tuned for good visual appearance on screen and in print.
 */
export const DEFAULT_FIELD_PIXEL_SIZES: Record<string, { w: number; h: number }> = {
  'signature-draw': { w: 220, h: 72 },
  'signature-upload': { w: 220, h: 72 },
  'signature-type': { w: 180, h: 28 }, // Tighter default — signatures should feel compact, not in a big empty box
  'date': { w: 120, h: 18 },
  'name': { w: 180, h: 18 },
  'text': { w: 220, h: 32 },
  'initials': { w: 70, h: 32 },
  'checkbox': { w: 22, h: 22 },
  'image': { w: 200, h: 150 },
  // Annotation types (Pass 2) — wiring agent may override width/height for these
  'highlight': { w: 120, h: 18 },
  'underline': { w: 120, h: 18 },
  'strikeout': { w: 120, h: 18 },
  'rect': { w: 120, h: 80 },
  'ellipse': { w: 120, h: 80 },
  'cross': { w: 64, h: 64 },
  'text-bubble': { w: 180, h: 72 },
  'ink': { w: 60, h: 60 }, // bounding box placeholder; real size comes from stroke
};

/**
 * Get sensible default PDF-point dimensions for a new field.
 */
export function getDefaultFieldSize(
  type: SigningField['type'],
  scale: number
): { width: number; height: number } {
  const px = DEFAULT_FIELD_PIXEL_SIZES[type] ?? { w: 140, h: 32 };
  return pixelsToPdfSize(px.w, px.h, scale);
}

/**
 * Compute a compact, content-aware size for text-based fields (especially signature-type).
 * This addresses the common complaint that signature boxes feel too big and empty
 * compared to the actual word being typed.
 */
export function getCompactTextFieldSize(
  type: SigningField['type'],
  value: string,
  fontSize: number,
  scale: number
): { width: number; height: number } {
  const tight = getCompactTextFieldSizeInPoints(type, value, fontSize);
  return pixelsToPdfSize(tight.width, tight.height, scale);
}

/**
 * Same as above but returns size directly in PDF points (no scale needed).
 * Useful for auto-fitting while editing in the properties panel.
 */
export function getCompactTextFieldSizeInPoints(
  type: SigningField['type'],
  value: string,
  fontSize: number,
  fieldStyle?: Partial<SigningField>
): { width: number; height: number } {
  const field = {
    id: 'measure',
    type,
    pageIndex: 0,
    x: 0,
    y: 0,
    width: 80,
    height: 24,
    ...fieldStyle,
    value,
    fontSize,
  } as SigningField;
  return measureTextFieldPdfSize(field, value);
}

export interface ResizeDimensionsInput {
  startWidth: number;
  startHeight: number;
  dx: number;
  dy: number;
  lockAspectRatio: boolean;
  minWidth?: number;
  minHeight?: number;
}

/**
 * Compute new width/height during bottom-right resize.
 * When lockAspectRatio is true, the dominant pointer delta drives proportional scaling.
 */
export function computeResizeDimensions({
  startWidth,
  startHeight,
  dx,
  dy,
  lockAspectRatio,
  minWidth = 18,
  minHeight = 18,
}: ResizeDimensionsInput): { width: number; height: number } {
  if (!lockAspectRatio) {
    return {
      width: Math.max(minWidth, startWidth + dx),
      height: Math.max(minHeight, startHeight + dy),
    };
  }

  const aspectRatio = startWidth / startHeight;
  const widthFromDx = dx;
  const widthFromDy = dy * aspectRatio;

  let width: number;
  let height: number;

  if (Math.abs(widthFromDx) >= Math.abs(widthFromDy)) {
    width = startWidth + dx;
    height = width / aspectRatio;
  } else {
    height = startHeight + dy;
    width = height * aspectRatio;
  }

  if (width < minWidth) {
    width = minWidth;
    height = width / aspectRatio;
  }
  if (height < minHeight) {
    height = minHeight;
    width = height * aspectRatio;
  }

  return { width, height };
}

/**
 * Clamp a field so it stays fully inside the page after drag/resize.
 */
export function clampFieldToPage(
  field: SigningField,
  pageWidth: number,
  pageHeight: number
): SigningField {
  const minSize = field.type === 'checkbox' ? 14 : 18;

  let { x, y, width, height } = field;

  // Prevent going outside left/right
  if (x < 0) x = 0;
  if (x + width > pageWidth) {
    x = Math.max(0, pageWidth - width);
  }

  // Prevent going outside top/bottom (remember y=0 is bottom)
  if (y < 0) y = 0;
  if (y + height > pageHeight) {
    y = Math.max(0, pageHeight - height);
  }

  // Enforce minimum sizes
  width = Math.max(minSize, Math.min(width, pageWidth - x));
  height = Math.max(minSize, Math.min(height, pageHeight - y));

  return { ...field, x, y, width, height };
}
